import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  MatchReviewDecision,
  MatchReviewList,
  MatchReviewSubmitResponse,
  RemediationReview,
} from '@/types/match-review';
import { useReviewQueue, type ReviewMutationFailure } from './useReviewQueue';

export interface UseMatchReviewsResult {
  data: MatchReviewList | null;
  isLoading: boolean;
  isMutating: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Record (or change) the verdict on one row. Updates the row in place. */
  decide: (
    reviewId: string,
    decision: MatchReviewDecision,
    note?: string | null
  ) => Promise<RemediationReview | null>;
  /** Stamp the AI's decision on every undecided row. */
  acceptAi: () => Promise<void>;
  /** Apply the verdicts; the caller refreshes the report afterwards. */
  submit: () => Promise<MatchReviewSubmitResponse | null>;
}

const LOAD_FALLBACK = 'Failed to load match reviews';
const DECIDE_FAILURE: ReviewMutationFailure = {
  title: 'Decision not saved',
  fallback: 'Failed to record the decision',
};
const ACCEPT_AI_FAILURE: ReviewMutationFailure = {
  title: 'Could not accept AI decisions',
  fallback: 'Failed to accept the AI decisions',
};
const SUBMIT_FAILURE: ReviewMutationFailure = {
  title: 'Review not submitted',
  fallback: 'Failed to submit the review',
};

/** Module-level so the queue's `refetch` keeps its identity across renders. */
const loadMatchReviews = (reportId: string) => vnextApiClient.getMatchReviews(reportId);

function replaceReview(list: MatchReviewList, updated: RemediationReview): MatchReviewList {
  const subjects = list.subjects.map((s) => ({
    ...s,
    reviews: s.reviews.map((r) => (r.id === updated.id ? updated : r)),
  }));
  const undecidedCount = subjects.reduce(
    (n, s) =>
      n + s.reviews.filter((r) => r.analystDecision === null && r.appliedAt === null).length,
    0
  );
  return { ...list, subjects, undecidedCount };
}

function submittedMessage(outcome: MatchReviewSubmitResponse): string {
  return outcome.rerunSubjectIds.length > 0
    ? `Remediation is re-running for ${outcome.rerunSubjectIds.length} subject(s) with your decisions applied.`
    : 'The report is continuing to synthesis.';
}

/**
 * The match-review queue for one report: the latest run's uncertain
 * matches per subject, with the analyst's verdicts. `null` reportId
 * disables the fetch (same idiom as useBackofficeFiles).
 */
export function useMatchReviews(reportId: string | null | undefined): UseMatchReviewsResult {
  const { data, isLoading, isMutating, error, refetch, setData, mutate } = useReviewQueue(
    reportId,
    loadMatchReviews,
    LOAD_FALLBACK
  );

  const decide = useCallback(
    (reviewId: string, decision: MatchReviewDecision, note?: string | null) =>
      mutate(
        DECIDE_FAILURE,
        (id) => vnextApiClient.decideMatchReview(id, reviewId, decision, note),
        (updated) => setData((prev) => (prev ? replaceReview(prev, updated) : prev))
      ),
    [mutate, setData]
  );

  const acceptAi = useCallback(async () => {
    await mutate(ACCEPT_AI_FAILURE, (id) => vnextApiClient.acceptAiMatchReviews(id), setData);
  }, [mutate, setData]);

  const submit = useCallback(
    () =>
      mutate(
        SUBMIT_FAILURE,
        (id) => vnextApiClient.submitMatchReview(id),
        async (outcome) => {
          notifications.show({
            title: 'Match review submitted',
            message: submittedMessage(outcome),
            color: 'green',
          });
          await refetch();
        }
      ),
    [mutate, refetch]
  );

  return { data, isLoading, isMutating, error, refetch, decide, acceptAi, submit };
}
